import { Suspense, type ReactNode } from 'react';
import { useLingui } from '@lingui/react/macro';
import { ShareFacebookIcon } from './assets/share-facebook-icon';
import { ShareXIcon } from './assets/share-x-icon';
import { ShareLinkedInIcon } from './assets/share-linkedin-icon';
import { ShareInstagramIcon } from './assets/share-instagram-icon';
import { DivWithPrimaryColorVariable } from './div-with-primary-color-variable';
import { WorldcooLogo } from '@worldcoo/ui/custom/worldcoo-logo';
import { Link, useRouteContext, useSearch } from '@tanstack/react-router';
import { PrivacySettingsLink } from '../../-components/privacy-settings-link';
import { UsercentricsPrimaryTheme } from './usercentrics-primary-theme';
import { usePortalDisplayContext } from './portal-display-context';

export function Footer() {
	const portalData = usePortalDisplayContext();
	if (portalData) {
		return <DisplayContextFooter />;
	}
	return (
		<Suspense>
			<SuspensedFooter />
		</Suspense>
	);
}

function DisplayContextFooter() {
	const { t } = useLingui();
	return (
		<FooterContent
			termsLink={<span className="underline">{t`Terms and Conditions`}</span>}
			privacyLink={<span className="underline">{t`Privacy Policy`}</span>}
			// Inert in the NGO area's preview dialog, where opening the consent layer over the editor would surprise.
			privacySettingsLink={<span className="underline">{t`Privacy Settings`}</span>}
		/>
	);
}

function SuspensedFooter() {
	const { portalId } = useRouteContext({ from: '__root__' });
	const { t } = useLingui();
	const { iframe } = useSearch({ from: '__root__' });
	if (iframe) return;

	return (
		<FooterContent
			termsLink={
				<Link to="/$portalId/legal-disclaimer" params={{ portalId }} className="underline" resetScroll>
					{t`Terms and Conditions`}
				</Link>
			}
			privacyLink={
				<Link
					to="/$portalId/privacy-policy"
					params={{ portalId }}
					className="underline"
					onClick={() => window.scrollTo(0, 0)}
				>
					{t`Privacy Policy`}
				</Link>
			}
			privacySettingsLink={<PrivacySettingsLink className="underline" />}
		/>
	);
}

function FooterContent(props: { termsLink: ReactNode; privacyLink: ReactNode; privacySettingsLink: ReactNode }) {
	const { t } = useLingui();
	const year = new Date().getFullYear();
	return (
		<DivWithPrimaryColorVariable className="bg-primary">
			{/* The only scope where `--primary` resolves to this portal's colour. */}
			<UsercentricsPrimaryTheme />
			<div className="mx-auto flex max-w-[1366px] flex-col items-center justify-between gap-6 px-5 py-6 sm:px-8 sm:py-8 lg:flex-row lg:px-20">
				<a
					href="https://www.worldcoo.com"
					className="text-primary-foreground flex items-center gap-0.5 text-sm font-light"
				>
					{t`Made by`}
					<WorldcooLogo
						className="text-primary-foreground h-[35px] w-auto"
						accentClassName="text-primary-foreground/60"
					/>
				</a>

				<div className="text-primary-foreground flex flex-col gap-3 text-sm font-light lg:flex-row">
					<span>{t`© ${year} WorldCoo. All rights reserved.`}</span>
					{props.termsLink}
					{props.privacyLink}
					{props.privacySettingsLink}
				</div>
				<nav>
					<ul className="flex gap-4">
						<li>
							<a href="https://www.facebook.com/worldcoo/" target="_blank" rel="noopener noreferrer">
								<span className="sr-only">Facebook</span>
								<ShareFacebookIcon className="text-primary" />
							</a>
						</li>

						<li>
							<a href="https://twitter.com/worldcoo" target="_blank" rel="noopener noreferrer">
								<span className="sr-only">Twitter</span>
								<ShareXIcon className="text-primary" />
							</a>
						</li>

						<li>
							<a href="https://www.instagram.com/worldcoo/" target="_blank" rel="noopener noreferrer">
								<span className="sr-only">Instagram</span>
								<ShareInstagramIcon className="text-primary" />
							</a>
						</li>

						<li>
							<a href="https://www.linkedin.com/company/worldcoo" target="_blank" rel="noopener noreferrer">
								<span className="sr-only">LinkedIn</span>
								<ShareLinkedInIcon className="text-primary" />
							</a>
						</li>
					</ul>
				</nav>
			</div>
		</DivWithPrimaryColorVariable>
	);
}
